import axios from 'axios';
import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type { ArchivePatientRequest, DeletePatientRequest } from '../types/patientContract';
import type {
  WorkspaceInitializationDto,
  WorkspaceOverviewRequestDto,
  WorkspaceOverviewResponseDto,
  WorkspaceActivityLogDto,
  WorkspaceLedgerEntryDto,
  WorkspaceHospitalDetailDto,
  WorkspaceHcpDetailDto,
  WorkspacePatientDetailDto,
  WorkspaceAssistanceCaseDetailDto,
  WorkspaceCaseLedgerEntryDto,
  WorkspaceAuditLogResponseDto,
  WorkspaceAuditLogFilterDto,
  WorkspaceAuditFilterOptionsDto,
  CanCreateAssistanceCaseResultDto,
} from '../types/workspace';

// ─────────────────────────────────────────────────────────────
// Workspace Service
// ─────────────────────────────────────────────────────────────

export const workspaceService = {
  /**
   * Initialize workspace context for a user in a program.
   * Called on login/program entry to load all required data.
   */
  async initializeWorkspace(programId: string): Promise<ApiResponse<WorkspaceInitializationDto>> {
    const response = await apiClient.get<ApiResponse<WorkspaceInitializationDto>>(
      `/programs/${programId}/workspace/initialize`
    );
    return response.data;
  },

  /**
   * Unified workspace overview: returns filter-scoped KPI summary counts + paginated entity list.
   * Replaces the former separate getStats() and getEntityHierarchy() calls.
   * Both counts and list rows come from the same filter, guaranteeing consistency.
   */
  async getOverview(
    programId: string,
    request: WorkspaceOverviewRequestDto
  ): Promise<ApiResponse<WorkspaceOverviewResponseDto>> {
    const response = await apiClient.post<ApiResponse<WorkspaceOverviewResponseDto>>(
      `/programs/${programId}/workspace/overview`,
      request
    );
    return response.data;
  },

  /**
   * Get recent activity logs for the workspace.
   * Filters by date (defaults to today if not specified).
   */
  async getRecentActivity(
    programId: string,
    limit: number = 50,
    date?: string  // YYYY-MM-DD format
  ): Promise<ApiResponse<WorkspaceActivityLogDto[]>> {
    const params: Record<string, string | number> = { limit };
    if (date) {
      params.date = date;
    }
    const response = await apiClient.get<ApiResponse<WorkspaceActivityLogDto[]>>(
      `/programs/${programId}/workspace/activity`,
      { params }
    );
    return response.data;
  },

  /**
   * Get ledger entries for the workspace.
   * Filters by date (defaults to today if not specified).
   */
  async getRecentLedger(
    programId: string,
    limit: number = 50,
    date?: string  // YYYY-MM-DD format
  ): Promise<ApiResponse<WorkspaceLedgerEntryDto[]>> {
    const params: Record<string, string | number> = { limit };
    if (date) {
      params.date = date;
    }
    const response = await apiClient.get<ApiResponse<WorkspaceLedgerEntryDto[]>>(
      `/programs/${programId}/workspace/ledger`,
      { params }
    );
    return response.data;
  },

  /**
   * Get entity detail for the right panel.
   */
  async getEntityDetail<T = unknown>(
    programId: string,
    entityType: string,
    entityId: string
  ): Promise<ApiResponse<T>> {
    const response = await apiClient.get<ApiResponse<T>>(
      `/programs/${programId}/workspace/entities/${entityType}/${entityId}`
    );
    return response.data;
  },

  /**
   * Refresh specific workspace sections.
   */
  async refreshWorkspace(
    programId: string,
    sections: string[]
  ): Promise<ApiResponse<WorkspaceInitializationDto>> {
    const response = await apiClient.post<ApiResponse<WorkspaceInitializationDto>>(
      `/programs/${programId}/workspace/refresh`,
      sections
    );
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────
  // Entity Detail Methods (for Detail Panel)
  // ─────────────────────────────────────────────────────────────

  /**
   * Get hospital detail for the right panel.
   */
  async getHospitalDetail(
    programId: string,
    hospitalId: string
  ): Promise<ApiResponse<WorkspaceHospitalDetailDto>> {
    const response = await apiClient.get<ApiResponse<WorkspaceHospitalDetailDto>>(
      `/programs/${programId}/workspace/hospitals/${hospitalId}/detail`
    );
    return response.data;
  },

  /**
   * Get HCP detail for the right panel.
   */
  async getHcpDetail(
    programId: string,
    hcpId: string
  ): Promise<ApiResponse<WorkspaceHcpDetailDto>> {
    const response = await apiClient.get<ApiResponse<WorkspaceHcpDetailDto>>(
      `/programs/${programId}/workspace/hcps/${hcpId}/detail`
    );
    return response.data;
  },

  /**
   * Get patient detail for the right panel.
   */
  async getPatientDetail(
    programId: string,
    patientId: string
  ): Promise<ApiResponse<WorkspacePatientDetailDto>> {
    const response = await apiClient.get<ApiResponse<WorkspacePatientDetailDto>>(
      `/programs/${programId}/workspace/patients/${patientId}/detail`
    );
    return response.data;
  },

  /**
   * Get assistance case detail with full financial data.
   */
  async getAssistanceCaseDetail(
    programId: string,
    caseId: string
  ): Promise<ApiResponse<WorkspaceAssistanceCaseDetailDto>> {
    const response = await apiClient.get<ApiResponse<WorkspaceAssistanceCaseDetailDto>>(
      `/programs/${programId}/workspace/cases/${caseId}/detail`
    );
    return response.data;
  },

  /**
   * Get case ledger entries with date filtering.
   */
  async getCaseLedger(
    programId: string,
    caseId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<ApiResponse<WorkspaceCaseLedgerEntryDto[]>> {
    const response = await apiClient.get<ApiResponse<WorkspaceCaseLedgerEntryDto[]>>(
      `/programs/${programId}/workspace/cases/${caseId}/ledger`,
      { params: { fromDate, toDate } }
    );
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────
  // Entity CRUD Methods
  // ─────────────────────────────────────────────────────────────

  /**
   * Create a new entity (Hospital, HCP, Patient, or Case).
   * Uses dynamic form schema for data validation.
   */
  async createEntity(
    programId: string,
    payload: {
      entityType: 'hospital' | 'hcp' | 'patient' | 'case';
      formId: string;
      parentId?: string;
      parentType?: 'hospital' | 'hcp' | 'patient';
      formData: Record<string, unknown>;
    }
  ): Promise<ApiResponse<{ entityId: string }>> {
    const response = await apiClient.post<ApiResponse<{ entityId: string }>>(
      `/programs/${programId}/workspace/entities`,
      payload
    );
    return response.data;
  },

  /**
   * Update an existing entity.
   * Uses dynamic form schema for data validation.
   */
  async updateEntity(
    programId: string,
    entityType: 'hospital' | 'hcp' | 'patient' | 'case',
    entityId: string,
    payload: {
      formId: string;
      formData: Record<string, unknown>;
    }
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.put<ApiResponse<void>>(
      `/programs/${programId}/workspace/entities/${entityType}/${entityId}`,
      payload
    );
    return response.data;
  },

  /**
   * Perform workflow transition on an entity.
   */
  async transitionEntity(
    programId: string,
    entityType: 'hospital' | 'hcp' | 'patient' | 'case',
    entityId: string,
    payload: {
      transitionId: string;
      comment?: string;
    }
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/programs/${programId}/workspace/entities/${entityType}/${entityId}/transition`,
      payload
    );
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────
  // Soft Delete Methods
  // ─────────────────────────────────────────────────────────────

  /**
   * Soft-delete a hospital. Backend validates hierarchy (no active HCPs/Patients).
   */
  async softDeleteHospital(
    programId: string,
    hospitalId: string
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/programs/${programId}/workspace/hospitals/${hospitalId}`
    );
    return response.data;
  },

  /**
   * Soft-delete an HCP. Backend validates hierarchy (no active Patients).
   */
  async softDeleteHcp(
    programId: string,
    hcpId: string
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/programs/${programId}/workspace/hcps/${hcpId}`
    );
    return response.data;
  },

  /**
   * Soft-delete a patient. Patient must be ARCHIVED first. Reason is mandatory.
   */
  async softDeletePatient(
    programId: string,
    patientId: string,
    request: DeletePatientRequest
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/programs/${programId}/workspace/patients/${patientId}`,
      { data: request }
    );
    return response.data;
  },

  /**
   * Archive a patient (status → ARCHIVED). Reason is mandatory.
   */
  async archivePatient(
    programId: string,
    patientId: string,
    request: ArchivePatientRequest
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/programs/${programId}/workspace/patients/${patientId}/archive`,
      request
    );
    return response.data;
  },

  /**
   * Unarchive a patient (status ARCHIVED -> ACTIVE). Reason is mandatory.
   */
  async unarchivePatient(
    programId: string,
    patientId: string,
    request: ArchivePatientRequest
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/programs/${programId}/workspace/patients/${patientId}/unarchive`,
      request
    );
    return response.data;
  },

  /**
   * Restore a deleted patient (status DELETED -> ACTIVE). Reason is mandatory.
   */
  async restorePatient(
    programId: string,
    patientId: string,
    request: ArchivePatientRequest
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/programs/${programId}/workspace/patients/${patientId}/restore`,
      request
    );
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────
  // Audit Log Methods
  // ─────────────────────────────────────────────────────────────

  /**
   * Get paginated audit logs for a program with filtering.
   */
  async getAuditLogs(
    programId: string,
    filter: WorkspaceAuditLogFilterDto
  ): Promise<ApiResponse<WorkspaceAuditLogResponseDto>> {
    const response = await apiClient.post<ApiResponse<WorkspaceAuditLogResponseDto>>(
      `/programs/${programId}/workspace/audit-logs`,
      filter
    );
    return response.data;
  },

  /**
   * Get distinct entity names and actions for audit log filter dropdowns.
   */
  async getAuditFilterOptions(
    programId: string
  ): Promise<ApiResponse<WorkspaceAuditFilterOptionsDto>> {
    const response = await apiClient.get<ApiResponse<WorkspaceAuditFilterOptionsDto>>(
      `/programs/${programId}/workspace/audit-logs/filter-options`
    );
    return response.data;
  },

  // ─────────────────────────────────────────────────────────────
  // Assistance Case Creation Check
  // ─────────────────────────────────────────────────────────────

  /**
   * Check if an assistance case can be created for a patient.
   * Validates patient's workflow step and user permissions.
   *
   * The backend returns 403 for a "not eligible yet" result (e.g. patient
   * not yet approved) — that's a normal read outcome here, not an error, so
   * it's unwrapped from the axios error rather than thrown.
   */
  async canCreateAssistanceCase(
    programId: string,
    patientId: string
  ): Promise<ApiResponse<CanCreateAssistanceCaseResultDto>> {
    try {
      const response = await apiClient.get<ApiResponse<CanCreateAssistanceCaseResultDto>>(
        `/programs/${programId}/workspace/patients/${patientId}/can-create-assistance-case`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403 && error.response.data) {
        return error.response.data as ApiResponse<CanCreateAssistanceCaseResultDto>;
      }
      throw error;
    }
  },
};
